import React, { useContext, useEffect } from 'react'
import { makeStyles, Grid, Typography } from '@material-ui/core'
import { cloneDeep } from 'lodash'
import MarblesChart from '../components/MarblesChart/index'
import { PieChart } from 'react-minimal-pie-chart'
import generateRiskCounts from '../ultils/generateRiskCounts'
import { Context as PortfolioContext } from '../contexts/portfolio'
import { RISK_TYPE } from '../ultils/chart-d3-utils'

const useStyles = makeStyles({
  visArea: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 260,
    width: '100%',
  },
  chartContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '28px',
    backgroundColor: '#fff',
    border: '1px solid #DFE4EB',
    boxSizing: 'border-box',
    height: 'auto',
    minHeight: 520,
    borderRadius: 5,
  },
  topRow: {
    display: 'flex',
    gap: 24,
    alignItems: 'stretch',
    marginBottom: 20,
  },
  statsCard: {
    flex: 1,
    backgroundColor: '#EEF3F7',
    border: '1px solid #E1E5EB',
    borderRadius: 8,
    padding: '16px 18px',
    minHeight: 110,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  statsTitle: {
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '26px',
    color: '#1B3963',
  },
  statsDivider: {
    height: 1,
    backgroundColor: '#D9DEE6',
    margin: '12px 0',
  },
  statsGrid: {
    display: 'grid',
    columnGap: 24,
    rowGap: 12,
  },
  statsItem: {
    minWidth: 0,
  },
  statsValue: {
    fontWeight: 600,
    fontSize: '28px',
    lineHeight: '36px',
    color: '#181818',
  },
  statsLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#474747',
  },
  statsDot: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    border: '2px solid #FFFFFF',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
  },
  donutWrapper: {
    width: 170,
    minWidth: 170,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  donutChart: {
    width: 170,
    height: 170,
  },
  donutCenter: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '36px',
    fontWeight: 600,
    color: '#181818',
    pointerEvents: 'none',
  },
  noDataContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  noDataText: {
    textAlign: 'center',
    width: '50%',
  },
})

const PortfolioLastWeek = ({ tickers, riskType }) => {
  const classes = useStyles()

  const { state } = useContext(PortfolioContext)
  // TODO: replace these 2 lines in production!
  // const LAST_WEEK = new Date('2021-02-26T03:24:00')
  const LAST_WEEK = new Date(new Date().setDate(new Date().getDate() - 7))

  const [increasingRiskTickers, decreasingRiskTickers] = cloneDeep(
    tickers,
  ).reduce(
    (acc, curr) => {
      const [inc, dec] = acc
      const changeValue =
        riskType === RISK_TYPE.CREDIT
          ? curr.output_credit_change
          : curr.output_change
      if (changeValue < 0 && curr.date > LAST_WEEK) {
        return [[...inc, curr], dec]
      }
      if (changeValue > 0 && curr.date > LAST_WEEK) {
        return [inc, [...dec, curr]]
      }
      return acc
    },
    [[], []],
  )
console.log('tickers+++',tickers)
  return (
    <Grid container spacing={3}>
      {[
        {
          tickers: increasingRiskTickers,
          title: 'Last Week Higher Level of Risk',
        },
        {
          tickers: decreasingRiskTickers,
          title: 'Last Week Lower Level of Risk',
        },
      ].map(({ tickers, title }, i) => (
        <Grid item xs={12} md={6} key={i}>
          <div className={classes.chartContainer}>
            {(() => {
              const statsData = generateRiskCounts(tickers, riskType)
                .filter(cat => cat.title !== 'Total')
                .filter(cat => cat.count > 0)
              const total = tickers.length
              const donutData =
                statsData.length > 0
                  ? statsData.map(d => ({
                      title: d.title,
                      value: d.count,
                      color: d.color,
                    }))
                  : [{ title: 'Total', value: 1, color: '#D1D6DE' }]
              const columns = Math.min(4, Math.max(1, statsData.length))
              return (
                <div className={classes.topRow}>
                  <div className={classes.statsCard}>
                    <div className={classes.statsTitle}>{title}</div>
                    <div className={classes.statsDivider} />
                    <div
                      className={classes.statsGrid}
                      style={{
                        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                      }}
                    >
                      {statsData.map(stat => (
                        <div className={classes.statsItem} key={stat.title}>
                          <div className={classes.statsValue}>{stat.count}</div>
                          <div className={classes.statsLabelRow}>
                            <span>{stat.title}</span>
                            <span
                              className={classes.statsDot}
                              style={{ backgroundColor: stat.color }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={classes.donutWrapper}>
                    <PieChart
                      className={classes.donutChart}
                      animate
                      animationDuration={500}
                      animationEasing="ease-out"
                      lineWidth={22}
                      radius={80}
                      viewBoxSize={[200, 200]}
                      center={[100, 100]}
                      background="#D1D6DE"
                      data={donutData}
                    />
                    <div className={classes.donutCenter}>{total}</div>
                  </div>
                </div>
              )
            })()}
            <div className={classes.visArea}>
              {tickers.length === 0 ? (
                <div className={classes.noDataContainer}>
                  <Typography className={classes.noDataText}>
                    There is no change in risk status of any portfolio company,
                    looking back over the last seven days
                  </Typography>
                </div>
              ) : (
                <MarblesChart
                  isEmptyData={state.tickersInPortfolio.length === 0}
                  tickers={tickers}
                  group="none"
                  riskType={riskType}
                  height={260}
                />
              )}
            </div>
          </div>
        </Grid>
      ))}
    </Grid>
  )
}

export default PortfolioLastWeek
